/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { debounceUntilResolved } from "libs/utils/promises";
import { isFunction } from "lodash";

// Components
import BulkActions from 'shared/ui/components/listings/BulkActions.vue';
import ListActions from 'shared/ui/components/listings/ListActions.vue';
import LoaderButton from 'shared/ui/components/form-components/LoaderButton.vue';
import SmartTable from 'shared/ui/components/tables/SmartTable.vue';
import SmartTableData from 'shared/ui/components/tables/SmartTableData.vue';

// Mixins
import filterable from 'shared/ui/mixins/table/filterable';
import searchable from 'shared/ui/mixins/table/searchable';
import sortable from 'shared/ui/mixins/table/sortable';
import customizable from 'shared/ui/mixins/table/customizable';

const RETRIEVE_ALL_STRATEGY = 'retrieve_all';
const DEFAULT_COLUMN_WIDTH = 120;

const __vue_script__ = {
    name: 'FpTypedList',

    components: {
        BulkActions,
        ListActions,
        LoaderButton,
        SmartTable,
        SmartTableData
    },

    mixins: [filterable, searchable, sortable, customizable],

    props: {
        controller: {
            type: Object,
            required: true
        },

        event: {
            type: Object,
            required: true
        },

        module: {
            type: Object,
            required: true
        }
    },

    data() {
        const { resolvers, routeOptions } = this.controller;
        const { callToActions, crudOpts = {} } = routeOptions;

        const defaultResolver = resolvers.default || {};
        const { strategy, options: resolverOptions = {} } = defaultResolver;
        const { fpType } = resolverOptions;

        const showSearchBar = !resolverOptions.hideSearchBar;

        const reload = debounceUntilResolved(async () => {
            await this.$refs.itemsTable.unselectAll();
            await this.loadData();
            this.applyCriteriaLocally();
        })

        return {
            MAX_ITEMS_PER_PAGE: crudOpts.itemsPerPage || 25,
            callToActions,
            columns: this.$utils.blueprint.getCrudColumns(fpType, crudOpts),
            crudOpts,
            fpType,
            hasFullLocalData: true,
            items: [],
            loading: false,
            metadata: {},
            page: 1,
            ready: false,
            resolverOptions,
            showSearchBar,
            strategy,
            total: 0,
            selectable: !this.controller.notSelectable && !crudOpts.noBatchActions,
            resizable: Boolean(crudOpts.useCustomColumns?.enabled), // temporarily tied to custom columns,
            onReload: reload,
            unsubscribers: []
        };
    },

    computed: {
        hidePagination() {
            return this.total <= this.MAX_ITEMS_PER_PAGE;
        },

        isSearching() {
            return this.searchTerm.length > 0;
        }
    },

    async created() {
        try {
            if (this.fpType === 'pax-request') {
                // for pax-requests the fields come from both person and pax-request metadata
                const paxRequestMetadata = await this.$services.metadata.getMetadataForType(this.event._id, this.fpType);
                const personMetadata = await this.$services.metadata.getMetadataForType(this.event._id, 'person');
                this.metadata = { ...personMetadata, ...paxRequestMetadata };

                this.forceAllColumnsSortable = this.crudOpts.forceAllColumnsSortable || false;
            } else {
                this.metadata = await this.$services.metadata.getMetadataForType(this.event._id, this.fpType);
            }

            await this.loadColumns();
            this.loadData();

            this.unsubscribers.push(
                await this.$services.signal.addSignalListener(`document/${this.fpType}/update`, this.onDocUpdateSignal),
            )
        } catch (error) {
            console.error('[FpTypedList] Could not initialize:', error.message);
        }
    },

    beforeDestroy() {
        for (const unsub of this.unsubscribers ?? []) {
            if (isFunction(unsub)) {
                unsub();
            }
        }
    },

    methods: {
        /**
         * Loads data for the component.
         *
         * @param {number} [page=1] - The page number to load data for. Defaults to 1 if not provided.
         */
        async loadData(page = 1) {
            try {
                if (page === 1) {
                    this.$refs.itemsTable.resetDisplay();
                }
                if (this.loading) {
                    return;
                }

                this.loading = true;

                if (this.resolverOptions.appscriptUrl) {
                    this.hasFullLocalData = !this.resolverOptions?.paginated;
                    const params = {
                        skip: this.hasFullLocalData ? 0 : this.MAX_ITEMS_PER_PAGE * (page - 1),
                        limit: this.hasFullLocalData ? 1000 : this.MAX_ITEMS_PER_PAGE
                    };

                    if (this.resolverOptions.filterBy) {
                        const routeParams = this.$router.getRouteParams();
                        params.id = routeParams[this.resolverOptions.filterBy];
                    }

                    await this.loadWithAppscript(page, params);

                    return;
                }

                if (this.strategy === RETRIEVE_ALL_STRATEGY) {
                    this.hasFullLocalData = true;
                    // For now we only load all with the document service, so we need to retrieve all, not just MAX_ITEMS.
                    // We need to put a number limit to override the default and 1k is way above most platform limits + we don't want to get more than 1k item in a single call.
                    await this.loadWithDocumentService(page, { limit: 1000 });
                }

            } catch (error) {
                console.error('[FpTypedList] Could not load data:', error.message);
            } finally {
                this.loading = false;
                this.ready = true;
            }
        },

        onDocUpdateSignal(signal){
            const  { targetId: docId, document } = signal

            const index = this.items.findIndex(item => item._id === docId)
            if (index === -1) return

            this.$set(this.items, index, {
                ...this.items[index],
                ...document
            })
        },

        /**
         * Asynchronously loads data using an Appscript.
         *
         * @param {number} page - The page number to load.
         * @param {Object} params - Additional parameters for the request.
         */
        async loadWithAppscript(page, params) {
            if (this.sort === 'DESC') {
                // Based on can/retrieve-page.js declaration.
                // This needs to be standardized in the future.
                params.reverse = true;
            }

            if (this.sortField) {
                params.sort = this.sortField;
            }

            if (this.isSearching) {
                params.search = this.searchTerm;
                params.limit = this.getLimitForSearchPagination(this.MAX_ITEMS_PER_PAGE);
            }

            const appscriptResponse = await this.$services.appScripts.callAppScript(
                this.event._id,
                this.resolverOptions.appscriptUrl,
                params,
            );
            // the appscript may return an array of documents
            // or a "rows" key with an array of documents and "total_rows"
            let rows = [];
            let total_rows = 0;
            if (appscriptResponse.rows && Number.isInteger(appscriptResponse.total_rows)) {
                rows = appscriptResponse.rows;
                total_rows = appscriptResponse.total_rows;
            } else if (Array.isArray(appscriptResponse)) {
                rows = appscriptResponse;
                total_rows = appscriptResponse.length;
            } else {
                console.error('[FpTypedList] Could not read appscript response:', appscriptResponse);
            }

            await this.decorateRows(rows);

            if (this.hasFullLocalData) {
                this.storeFullData(page, rows);
            } else {
                this.items = rows;
                this.page = page;
                this.total = this.isSearching
                    ? this.setPaginationFields(this.isSearching, this.MAX_ITEMS_PER_PAGE, params.skip)
                    : total_rows;
            }
        },

        /**
         * Asynchronously loads data using the document service.
         *
         * @param {number} page - The page number to load.
         * @param {Object} params - Additional parameters for the document service.
         */
        async loadWithDocumentService(page, params) {
            const fpType = this.crudOpts.queryFpType || this.fpType;

            params.fields = this.resolverOptions.fields;
            params.cached = this.resolverOptions.preferCachedListResponse;

            const { items: rows } = await this.$services.documents.getAllDocsByType(this.event._id, fpType, params);
            await this.decorateRows(rows);
            this.storeFullData(page, rows);
        },

        /**
         * Store full data
         * @param {int} page - the page number
         * @param {Array} rows - The full data rows
         */
        storeFullData(page, rows) {
            this.fullItems = rows;
            this.total = this.fullItems.length;
            this.items = rows.slice(this.MAX_ITEMS_PER_PAGE * (page - 1), this.MAX_ITEMS_PER_PAGE * page);
            this.page = page;
        },

        /**
         * Decorates the rows of the component.
         *
         * @param {Array} rows - The rows to be decorated.
         */
        async decorateRows(rows) {
            try {
                for (const col of this.columns) {
                    if (!col.appscriptUrl) {
                        continue;
                    }

                    const params = {};

                    // col.params is built as { scriptParam: eachRowFieldProperty }
                    // meaning that "scriptParam" is the name of the parameter that the appscript expects
                    // and "eachRowFieldProperty" is the name of the property in each row that will be passed as the value
                    // making "scriptParam" an array of all the values of "eachRowFieldProperty"
                    for (const param of Object.keys(col.params)) {
                        params[param] = rows.map(row => row[col.params[param]]);
                    }

                    const data = await this.$services.appScripts.callAppScript(this.event._id, col.appscriptUrl, params);

                    rows.forEach(row => {
                        for (const param of Object.keys(col.params)) {
                            // Populate the row with the data from the appscript
                            // that must return an object with property with key the "eachRowFieldProperty" value
                            row[col.field] = data[row[col.params[param]]] || col.default;
                        }
                    });
                }
            } catch (error) {
                console.error('[FpTypedList] Could not decorate data:', error.message);
            }
        },

        /**
         * In the case of full local data, apply the search, filter and sort criteria.
         */
        applyCriteriaLocally() {
            this.$refs.itemsTable.resetDisplay();
            this.fullCriteriaItems = undefined;

            const searched = !!this.searchTerm;
            const filtered = Object.values(this.activeFilters).some(
                filter => Array.isArray(filter) && filter.length
            );

            if (!searched && !filtered) {
                this.items = this.fullItems.slice(0, this.MAX_ITEMS_PER_PAGE);
            }

            this.applySearchCriteriaLocally();
            this.applySortCriteriaLocally();
            this.applyFilterCriteriaLocally();

            this.onPageChange(1);
        },

        /**
         * Handle page change.
         * @param page page number ( 1 based )
         */
        onPageChange(page) {
            if (this.hasFullLocalData) {
                this.page = page;
                this.items = this.fullCriteriaItems ?? this.fullItems;
            } else {
                this.loadData(page);
            }
        },

        /**
         * Checks if the given item meets the selection criteria.
         *
         * @param {Object} item - The item to be checked.
         *
         * @returns {boolean} - Returns true if the item meets the selection criteria, otherwise false.
         */
        itemSelectionCheck(item) {
            if (!this.crudOpts?.selectionConditions) {
                return true;
            }
            const rules = this.crudOpts.selectionConditions;
            return this.$utils.objects.areConditionsMet(rules, item);
        },

        /**
         * Get the classes for the table header cell.
         *
         * @param {Object} column - column specification
         * @param {Object} columnClasses - mapping from column field to classes
         */
        getTableHeaderClasses(column, columnClasses) {
            // Only mark columns as sortable automatically when we have the full local dataset.
            if (this.forceAllColumnsSortable && this.hasFullLocalData) {
                column.sortable = true;
            }

            const classes = [
                ...(this.$utils.tables.cellClasses(column) || []),
                ...(this.$utils.tables.getCellSortClass(column, this.sort, this.sortBy) || []),
                ...(columnClasses[column.field] || [])
            ];

            return classes;
        },

        /**
         * Get the styles for the table header cell.
         *
         * @param {Object} column - column specification
         * @param {Object} columnWidths - mapping from column field to width
         */
        getTableHeaderStyle(column, columnWidths) {
            return {
                width: `${columnWidths?.[column.field] || DEFAULT_COLUMN_WIDTH}px`
            };
        },
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"fp-typed-list"},[_c('smart-table',{ref:"itemsTable",class:_vm.fpType,attrs:{"track-by":"_id","hide-pagination":_vm.hidePagination,"show-page-numbers":!_vm.isSearching,"items-per-page":_vm.MAX_ITEMS_PER_PAGE,"items":_vm.items,"full-list-provided":_vm.hasFullLocalData,"selectable":_vm.selectable,"resizable":_vm.resizable,"id":_vm.fpType,"total-items":_vm.total || _vm.items.length,"item-selection-check":_vm.itemSelectionCheck,"columns":_vm.columns},on:{"next":_vm.onPageChange,"previous":_vm.onPageChange,"select-page":_vm.onPageChange},scopedSlots:_vm._u([{key:"filters",fn:function(ref){return [(_vm.ready)?_c('list-filters',{attrs:{"controller":_vm.controller,"module":_vm.module,"event":_vm.event,"metadata":_vm.metadata},model:{value:(_vm.activeFilters),callback:function ($$v) {_vm.activeFilters=$$v},expression:"activeFilters"}}):_vm._e(),(_vm.showSearchBar)?_c('search-bar',{attrs:{"show-no-result":false},model:{value:(_vm.searchTerm),callback:function ($$v) {_vm.searchTerm=$$v},expression:"searchTerm"}}):_vm._e()]}},(_vm.showCustomColumnsButton)?{key:"custom-columns",fn:function(ref){return [_c('custom-columns-button',{attrs:{"eid":_vm.event._id,"columns":_vm.allCustomColumns,"selected-fields":_vm.selectedCustomColumns,"settings-key":_vm.customColumnsSettingsKey},on:{"columns":_vm.handleColumnsChange}})]}}:null,{key:"actions",fn:function(ref){return [(_vm.ready)?_c('list-actions',{attrs:{"controller":_vm.controller,"module":_vm.module,"event":_vm.event,"metadata":_vm.metadata},on:{"reload":_vm.onReload}}):_vm._e(),_vm._t("actions",null,{"allItems":_vm.items})]}},{key:"bulk-actions",fn:function(ref){
var selectedItems = ref.selectedItems;
return [_vm._t("bulk-actions",null,{"selectedItems":selectedItems,"onReload":_vm.onReload}),_c('bulk-actions',{attrs:{"controller":_vm.controller,"module":_vm.module,"event":_vm.event,"selected-items":selectedItems},on:{"reload":_vm.onReload}}),_vm._t("actions-after",null,{"selectedItems":selectedItems,"allItems":_vm.items})]}},{key:"headers",fn:function(ref){
var columnWidths = ref.columnWidths;
var columnClasses = ref.columnClasses;
return _vm._l((_vm.columns),function(c){return _c('th',{class:_vm.getTableHeaderClasses(c, columnClasses),style:(_vm.getTableHeaderStyle(c, columnWidths)),attrs:{"data-id":c.field},on:{"click":function($event){return _vm.setSortable(c, c.field || c.label)}}},[_c('span',[_vm._v(_vm._s(_vm.$te(c.label) ? _vm.$t(c.label) : c.label))]),_c('i',{staticClass:"ic-icon"})])})}},{key:"row",fn:function(ref){
var item = ref.item;
var columnClasses = ref.columnClasses;
return _vm._l((_vm.columns),function(c){return _c('smart-table-data',{key:((c.field || c.label) + "_" + (item._id)),ref:((c.field || c.label) + "_" + (item._id)),refInFor:true,class:columnClasses[c.field],attrs:{"event":_vm.event,"metadata":_vm.metadata,"config":c,"item":item,"module":_vm.module}})})}}],null,true)})],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "FpTypedList.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__